import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Dialog, Input, Select } from '@dreamtree-org/twreact-ui';
import { Plus } from 'lucide-react';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import DataTable from '@/components/DataTable';
import { platformGet, platformSend, errorOf } from './platformApi';

export const STATUS_VARIANT = {
  active: 'success', draft: 'secondary', provisioning: 'primary', provisioning_failed: 'danger',
  suspended: 'warning', maintenance: 'warning', archived: 'secondary',
};

function dbSummary(r) {
  const check = r.lastCheckOk === 0 ? ' · check failed' : '';
  return `${r.dbMode} · ${r.dbStatus}${check}`;
}

function slugFrom(name) {
  let s = String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  while (s.startsWith('-')) s = s.slice(1);
  while (s.endsWith('-')) s = s.slice(0, -1);
  return s.slice(0, 63);
}

/** P04 (first slice) — company + default branch as a draft; the database comes next, on the detail page. */
function NewCompanyDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ displayName: '', slug: '', branchCode: 'HQ', branchName: 'Head Office' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugState, setSlugState] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ displayName: '', slug: '', branchCode: 'HQ', branchName: 'Head Office' });
    setSlugTouched(false);
    setErr('');
  }, [open]);

  useEffect(() => {
    if (!form.slug) { setSlugState(null); return undefined; }
    const t = setTimeout(() => {
      platformGet('/companies/slug-available', { slug: form.slug }).then(setSlugState).catch(() => setSlugState(null));
    }, 300);
    return () => clearTimeout(t);
  }, [form.slug]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const company = await platformSend('post', '/companies', {
        displayName: form.displayName, slug: form.slug, branch: { code: form.branchCode, name: form.branchName },
      });
      onCreated(company);
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog isOpen={open} onClose={onClose} title="New company" size="md">
      <form className="space-y-3" onSubmit={submit}>
        {err && <Alert variant="error">{err}</Alert>}
        <Input
          label="Company name"
          value={form.displayName}
          onChange={(e) => set({ displayName: e.target.value, ...(slugTouched ? {} : { slug: slugFrom(e.target.value) }) })}
          required
        />
        <div>
          <Input label="Sign-in address (slug)" value={form.slug} onChange={(e) => { setSlugTouched(true); set({ slug: e.target.value.toLowerCase() }); }} required />
          <p className={`text-xs mt-1 ${slugState && !slugState.available ? 'text-red-600' : 'text-secondary-500'}`}>
            {slugState && !slugState.available ? slugState.reason : `Users will sign in at /c/${form.slug || 'slug'}/login`}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="Default branch code" value={form.branchCode} onChange={(e) => set({ branchCode: e.target.value.toUpperCase() })} required />
          <Input label="Default branch name" value={form.branchName} onChange={(e) => set({ branchName: e.target.value })} required />
        </div>
        <p className="text-xs text-secondary-500">The company starts as a draft. Attach or provision its database on the next screen; nobody can sign in until it is active.</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || (slugState && !slugState.available)}>{busy ? 'Creating…' : 'Create draft'}</Button>
        </div>
      </form>
    </Dialog>
  );
}

/** P03 — company directory. */
export default function PlatformCompanies() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || '';
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    platformGet('/companies', { status }).then(setRows).catch((ex) => setErr(errorOf(ex).message));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: 'displayName', label: 'Company', render: (r) => <Link to={`/platform/companies/${r.id}`} className="font-medium text-primary-600 hover:underline">{r.displayName}</Link> },
    { key: 'slug', label: 'Slug', render: (r) => <code className="text-xs">{r.slug}</code> },
    { key: 'status', label: 'Status', render: (r) => <Badge size="sm" variant={STATUS_VARIANT[r.status] || 'secondary'}>{r.status.replace('_', ' ')}</Badge> },
    { key: 'branchCount', label: 'Branches' },
    {
      key: 'dbStatus', label: 'Database', searchable: false,
      render: (r) => (r.dbStatus ? dbSummary(r) : <span className="text-secondary-500">none</span>),
    },
  ];

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Companies"
        subtitle="Every company has its own database and branches. Lifecycle changes are audited."
        actions={<Button size="sm" onClick={() => setCreating(true)}><Plus size={14} className="mr-1 inline" /> New company</Button>}
      />
      {err && <Alert variant="error">{err}</Alert>}
      <DataTable
        data={rows}
        columns={columns}
        search={{ placeholder: 'Search companies…', keys: ['displayName', 'slug'] }}
        emptyText="No companies yet."
        toolbar={(
          <div className="w-full sm:w-56">
            <Select
              aria-label="Filter by status"
              value={status}
              onChange={(v) => setParams(v ? { status: v } : {})}
              options={[{ value: '', label: 'All statuses' }, ...Object.keys(STATUS_VARIANT).map((s) => ({ value: s, label: s.replace('_', ' ') }))]}
            />
          </div>
        )}
      />
      <NewCompanyDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(c) => { setCreating(false); nav(`/platform/companies/${c.id}`); }}
      />
    </PageShell>
  );
}
